import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, FileText, CheckSquare, Users, Calendar,
  BarChart3, Settings, Activity, Building2, Database, Shield,
  ChevronLeft, ChevronRight, Eye, Lock, Archive, Workflow,
  ChevronDown,
} from 'lucide-react';
import { useSidebarStore } from '../../store/sidebarStore';
import { useAuthStore } from '../../store/authStore';
import { useSystemSettingsStore } from '../../store/systemSettingsStore';
import { useCapabilities } from '../../hooks/useCapabilities';
import { getAssetUrl } from '../../utils/url';

interface MenuItem {
  label: string;
  icon: React.ReactNode;
  href: string;
  capability?: string;
  capabilities?: string[];
  levelMax?: number;
  levelExact?: number;
  children?: MenuItem[];
}

const menuItems: MenuItem[] = [
  { label: 'Dashboard', icon: <LayoutDashboard className="w-5 h-5" />, href: '/dashboard' },
  { label: 'Laporan Masalah', icon: <FileText className="w-5 h-5" />, href: '/laporan', capability: 'create_laporan' },
  { label: 'Arsip Laporan', icon: <Archive className="w-5 h-5" />, href: '/arsip', capability: 'archive' },
  {
    label: 'Persetujuan Laporan',
    icon: <CheckSquare className="w-5 h-5" />,
    href: '/validasi',
    capability: 'validate',
    children: [
      {
        label: 'Persetujuan Dekan / Ketua Lembaga',
        icon: <Shield className="w-4 h-4" />,
        href: '/validasi/level1',
        capability: 'validate',
        levelMax: 2,
        levelExact: 2,
      },
      {
        label: 'Persetujuan Wakil Rektor',
        icon: <Shield className="w-4 h-4" />,
        href: '/validasi/level2',
        capability: 'validate',
        levelMax: 1,
      },
      // HAPUS BARIS INI:
      // { label: 'Alur Persetujuan', icon: <Shield .../>, href: '/validasi/workflow', ... },
    ],
  },
  { label: 'Rapat Pimpinan', icon: <Calendar className="w-5 h-5" />, href: '/rapim', capabilities: ['manage_rapim', 'schedule_rapim', 'make_rapim_decision'] },
  { label: 'Monitoring', icon: <Eye className="w-5 h-5" />, href: '/monitoring', capability: 'access_monitoring' },
  { label: 'Laporan & Analitik', icon: <BarChart3 className="w-5 h-5" />, href: '/reports/builder', capability: 'access_report_builder' },
  {
    label: 'Admin',
    icon: <Settings className="w-5 h-5" />,
    href: '/admin',
    capability: 'manage_users',
    children: [
      { label: 'Pengguna', icon: <Users className="w-4 h-4" />, href: '/admin/users', capability: 'manage_users' },
      { label: 'Role Management', icon: <Shield className="w-4 h-4" />, href: '/admin/roles', capability: 'manage_users' },
      { label: 'Unit Organisasi', icon: <Building2 className="w-4 h-4" />, href: '/admin/units', capability: 'manage_units' },
      { label: 'Master Data', icon: <Database className="w-4 h-4" />, href: '/admin/master', capability: 'manage_master_data' },
      { label: 'Role Scopes', icon: <Lock className="w-4 h-4" />, href: '/admin/role-scopes', capability: 'manage_users' },
      { label: 'Capabilities', icon: <Shield className="w-4 h-4" />, href: '/admin/capabilities', capability: 'manage_users' },
      { label: 'Workflow', icon: <Workflow className="w-4 h-4" />, href: '/admin/workflow', capability: 'manage_settings' },
      { label: 'Pengaturan', icon: <Settings className="w-4 h-4" />, href: '/admin/settings', capability: 'manage_settings' },
      { label: 'Log Aktivitas', icon: <Activity className="w-4 h-4" />, href: '/admin/logs', capability: 'view_activity_log' },
    ],
  },
];

// Collapsed popup menggunakan Portal
const CollapsedPopup: React.FC<{
  item: MenuItem;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  onNavigate: () => void;
  isActive: (href: string) => boolean;
  canAccessItem: (item: MenuItem) => boolean;
}> = ({ item, buttonRef, onClose, onNavigate, isActive, canAccessItem }) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top,
        left: rect.right + 8,
      });
    }
  }, [buttonRef]);

  const filteredChildren = (item.children || []).filter(canAccessItem);
  if (filteredChildren.length === 0) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[9998]" onClick={onClose} />

      {/* Popup */}
      <div
        className="fixed z-[9999] w-56"
        style={{ top: `${position.top}px`, left: `${position.left}px` }}
      >
        <div className="bg-[#0C3B67] border border-white/15 rounded-xl shadow-2xl shadow-black/50 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-2.5 border-b border-white/10 bg-white/5">
            <p className="text-xs font-bold text-white uppercase tracking-wider">{item.label}</p>
          </div>

          {/* Items */}
          <div className="py-1.5 px-1.5">
            {filteredChildren.map((child) => (
              <Link
                key={child.href}
                to={child.href}
                onClick={() => {
                  onClose();
                  onNavigate();
                }}
                className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-xs font-medium transition-colors ${isActive(child.href)
                  ? 'bg-teal-light/20 text-white'
                  : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
              >
                {child.icon}
                <span>{child.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

const Sidebar: React.FC = () => {
  const { isExpanded, toggle, isMobileOpen, setMobileOpen } = useSidebarStore();
  const { user } = useAuthStore();
  const { settings } = useSystemSettingsStore();
  const caps = useCapabilities();
  const location = useLocation();
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [logoError, setLogoError] = useState(false);

  // Refs untuk setiap tombol collapsed menu
  const buttonRefs = useRef<Record<string, React.RefObject<HTMLButtonElement | null>>>({});

  const getButtonRef = (href: string) => {
    if (!buttonRefs.current[href]) {
      buttonRefs.current[href] = React.createRef<HTMLButtonElement | null>();
    }
    return buttonRefs.current[href];
  };

  // Tutup popup saat navigasi (di mode collapsed)
  // Atau jika navigasi ke menu yang tidak ada hubungan dengan submenu yang sedang dibuka
  useEffect(() => {
    if (!isExpanded) {
      setOpenSubmenu(null);
      return;
    }

    // Cek apakah current path masih bagian dari openSubmenu
    if (openSubmenu) {
      const currentParent = filteredItems.find(item => item.href === openSubmenu);
      const isChildActive = currentParent?.children?.some(child => location.pathname.startsWith(child.href.split('?')[0]));

      if (!isChildActive && !location.pathname.startsWith(openSubmenu)) {
        setOpenSubmenu(null);
      }
    }
  }, [location.pathname, isExpanded]);

  // Inisialisasi submenu yang terbuka berdasarkan path saat ini (saat load atau expanded)
  useEffect(() => {
    if (isExpanded && !openSubmenu) {
      const activeParent = filteredItems.find(item =>
        item.children?.some(child => location.pathname.startsWith(child.href.split('?')[0]))
      );
      if (activeParent) {
        setOpenSubmenu(activeParent.href);
      }
    }
  }, [isExpanded, location.pathname]);

  useEffect(() => {
    setLogoError(false);
  }, [settings.logo_url]);

  const canAccessItem = (item: MenuItem): boolean => {
    if (!item.capability && !item.capabilities && item.levelMax === undefined) {
      return true;
    }
    if (item.levelMax !== undefined && !caps.hasLevel(item.levelMax)) {
      return false;
    }
    if (item.levelExact !== undefined) {
      const userLevel = user?.role?.level ?? 99;
      if (userLevel !== item.levelExact && user?.role?.name !== 'admin') {
        return false;
      }
    }
    if (item.capability && caps.has(item.capability)) return true;
    if (item.capabilities && caps.hasAny(item.capabilities)) return true;
    if (user?.role?.name === 'admin') return true;
    return false;
  };

  const filteredItems = menuItems.filter(canAccessItem);

  const isActive = (href: string) => {
    const [path, query] = href.split('?');
    if (!location.pathname.startsWith(path)) return false;
    if (!query) return true;
    const currentParams = new URLSearchParams(location.search);
    const targetParams = new URLSearchParams(query);
    return Array.from(targetParams.entries()).every(([k, v]) => currentParams.get(k) === v);
  };

  const appName = settings.app_name || 'SIM-EVAL';
  const brandInitials = appName
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();
  const footerText = settings.footer_text || '© 2026 UBP Karawang';
  const logoUrl = getAssetUrl(settings.logo_url);
  const showLogo = logoUrl && !logoError;

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      <aside
        className={`fixed top-0 left-0 h-full bg-teal-dark text-white z-50 transition-all duration-300 flex flex-col ${isExpanded ? 'w-64' : 'w-16'
          } ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
          <div className="flex items-center gap-2 overflow-hidden">
            <div
              className={`w-8 h-8 flex-shrink-0 flex items-center justify-center font-black text-sm overflow-hidden ${showLogo ? 'bg-transparent rounded-md' : 'bg-teal-light rounded-lg'
                }`}
            >
              {showLogo ? (
                <img
                  src={logoUrl!}
                  alt="Logo"
                  className="w-full h-full object-contain"
                  onError={() => setLogoError(true)}
                />
              ) : (
                <span className="text-white">{brandInitials}</span>
              )}
            </div>
            {isExpanded && <span className="font-bold text-sm tracking-tight truncate">{appName}</span>}
          </div>
          <button onClick={toggle} className="p-1.5 rounded-lg hover:bg-white/10 hidden lg:block">
            {isExpanded ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-2">
          <ul className="space-y-1">
            {filteredItems.map((item) => (
              <li key={item.href}>
                {item.children ? (
                  // === MENU DENGAN SUB-MENU ===
                  isExpanded ? (
                    // EXPANDED: Tampilkan dropdown biasa
                    <div>
                      <button
                        onClick={() => setOpenSubmenu(openSubmenu === item.href ? null : item.href)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${isActive(item.href)
                          ? 'bg-teal-light/20 text-white'
                          : 'text-white/70 hover:bg-white/10 hover:text-white'
                          }`}
                      >
                        {item.icon}
                        <span className="flex-1 text-left">{item.label}</span>
                        <ChevronDown
                          className={`w-4 h-4 transition-transform duration-200 ${openSubmenu === item.href ? 'rotate-180' : ''
                            }`}
                        />
                      </button>

                      {/* Dropdown sub-menu */}
                      <div
                        className={`overflow-hidden transition-all duration-200 ${openSubmenu === item.href ? 'max-h-96 mt-1' : 'max-h-0'
                          }`}
                      >
                        <ul className="ml-4 space-y-1 border-l border-white/10 pl-3">
                          {item.children.filter(canAccessItem).map((child) => (
                            <li key={child.href}>
                              <Link
                                to={child.href}
                                onClick={() => setMobileOpen(false)}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${isActive(child.href)
                                  ? 'bg-teal-light/20 text-white'
                                  : 'text-white/60 hover:bg-white/10 hover:text-white'
                                  }`}
                              >
                                {child.icon}
                                {child.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    // Di dalam map filteredItems, bagian item.children && !isExpanded
                    // Ganti seluruh blok FlyoutMenu/collapsed menjadi:

                  ) : (
                    // COLLAPSED: Portal popup
                    <div>
                      <button
                        ref={getButtonRef(item.href) as any}
                        onClick={() => setOpenSubmenu(openSubmenu === item.href ? null : item.href)}
                        className={`flex items-center justify-center w-full px-3 py-2.5 rounded-lg transition-colors duration-200 ${isActive(item.href) || openSubmenu === item.href
                          ? 'bg-teal-light/20 text-white'
                          : 'text-white/70 hover:bg-white/10 hover:text-white'
                          }`}
                        title={item.label}
                      >
                        {item.icon}
                      </button>

                      {openSubmenu === item.href && (
                        <CollapsedPopup
                          item={item}
                          buttonRef={getButtonRef(item.href)}
                          onClose={() => setOpenSubmenu(null)}
                          onNavigate={() => setMobileOpen(false)}
                          isActive={isActive}
                          canAccessItem={canAccessItem}
                        />
                      )}
                    </div>
                  )
                ) : (
                  // === MENU TANPA SUB-MENU ===
                  <Link
                    to={item.href}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${isActive(item.href)
                      ? 'bg-teal-light/20 text-white border-l-2 border-teal-light'
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                      }`}
                    title={!isExpanded ? item.label : undefined}
                  >
                    {item.icon}
                    {isExpanded && <span>{item.label}</span>}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* Footer */}
        {isExpanded && (
          <div className="p-4 border-t border-white/10">
            <p className="text-xs text-white/40 text-center">{footerText}</p>
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;